import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearPinSelected, updatePin } from "redux/pinSlice";

import { RootState } from "redux/store";
import { selectLabel } from "utils/forge/extensions/custom-label";

import { getCurrentViewer } from "utils/forge";

import {
  MapPinGroupStatusTypeColor,
  Pin,
  PinGroupStatusType,
} from "interfaces/models/pin";
import useShowPinLabels from "../content/InspectionDetailPinGroup/leftBar/pins/hooks/useShowPinLabels";
import {
  useContextInspectionDetail,
  useInspectionDetailDispatch,
} from "../context/inspectionDetail";
import {
  DETAIL_MODE,
  DURATION_CLEAR_HIGHLIGHT,
  OBJECT_ACTIVE_COLOR,
  TARGET_OBJECT_GROUP_DETAIL_COLOR,
  Types,
} from "../context/inspectionDetailReducer";
import useBehaviorOtherObjects from "./useBehaviorOtherObjects";
import useBehaviorPinObjects from "./useBehaviorPinObjects";
import useBehaviorTargetObjects from "./useBehaviorTargetObjects";
import useGetPinObjectDbIds from "./useGetPinObjectDbIds";
import useGetTargetObjectDbIds from "./useGetTargetObjectDbIds";
import { useDeepCompareMemoize } from "use-deep-compare-effect";
import { flatten } from "lodash-es";

export const usePins = () => {
  const { pinSelected, pins, listSelected } = useSelector(
    (state: RootState) => state.pin
  );
  const { isLoadedViewer, isLoadedViewerModelData } = useSelector(
    (state: RootState) => state.forgeViewer
  );
  const {
    targetObjects,
    activeObjects,
    mode,
    isFilterPinNoGroup,
    viewerAddPinType,
    currentInspectionPins,
    pinsByMode,
    pinOfGroupIds,
    currentInspectionPinIds,
  } = useContextInspectionDetail();

  const dispatch = useDispatch();
  const { getDbIdsFromPins, getDbIdsWithColorFromPins } =
    useGetPinObjectDbIds();
  const { getDbIdsFromTargetObjects } = useGetTargetObjectDbIds();

  const { hideOtherObjectsWithExcludeDbIds } = useBehaviorOtherObjects();
  const {
    highlightTargetObjectsWithExcludeDbIds,
    clearHighlighTargetObjectByDbIds,
  } = useBehaviorTargetObjects();
  const { highlightPinObjects, clearHighlightPinObject } =
    useBehaviorPinObjects();

  useShowPinLabels({
    pins: pinsByMode,
  });

  const newPinObjectAdded = useMemo(
    () => ({
      dbIds: getDbIdsFromTargetObjects(activeObjects),
      color: OBJECT_ACTIVE_COLOR,
    }),
    [activeObjects, getDbIdsFromTargetObjects]
  );
  const targetObject = useMemo(
    () => ({
      dbIds: getDbIdsFromTargetObjects(targetObjects),
      color: TARGET_OBJECT_GROUP_DETAIL_COLOR,
    }),
    [targetObjects, getDbIdsFromTargetObjects]
  );
  const { pinObjects, dbIds } = useMemo(() => {
    const pinObjects = getDbIdsWithColorFromPins(currentInspectionPins);
    const dbIds = flatten(pinObjects.map((p) => p.dbIds));

    return { pinObjects, dbIds };
  }, [currentInspectionPins, getDbIdsWithColorFromPins]);

  const pinObjectSelected = useMemo(
    () => ({
      dbIds: getDbIdsFromPins(pinSelected ? [pinSelected] : []),
      color:
        MapPinGroupStatusTypeColor[
          pinSelected?.status ?? PinGroupStatusType.NotStarted
        ],
    }),
    [pinSelected, getDbIdsFromPins]
  );

  const listExistModePins = useMemo(
    () =>
      (isFilterPinNoGroup ? (pinsByMode as Pin[]) : pins).filter(
        (pin) =>
          !pinOfGroupIds.has(pin.id) || currentInspectionPinIds.has(pin.id)
      ),
    [
      currentInspectionPinIds,
      isFilterPinNoGroup,
      pinsByMode,
      pinOfGroupIds,
      pins,
    ]
  );

  const allPinObject = useMemo(() => {
    return getDbIdsWithColorFromPins(listExistModePins);
  }, [getDbIdsWithColorFromPins, listExistModePins]);

  // TODO: hide other objects exclude
  // + target objects, pin objects (detailPinGroup mode)
  // + all pin objects (addPinExist mode)
  useEffect(
    () => {
      if (!isLoadedViewer) {
        return;
      }

      let excludeDbIds: number[] = [...targetObject.dbIds, ...dbIds];
      if (mode === DETAIL_MODE.ADD_PIN_EXIST) {
        excludeDbIds = flatten(allPinObject.map((p) => p.dbIds));
      }
      const viewer = getCurrentViewer();
      if (viewer?.model?.is3d()) {
        hideOtherObjectsWithExcludeDbIds(excludeDbIds);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoadedViewer,
      mode,
      useDeepCompareMemoize([allPinObject, dbIds]),
      targetObject?.dbIds?.length,
      hideOtherObjectsWithExcludeDbIds,
    ]
  );

  // TODO: highlight target objects, pin objects
  useEffect(
    () => {
      if (!isLoadedViewerModelData) {
        return;
      }

      let _pinObjects = pinObjects;
      // TODO: highlight all pin in addPinExist mode
      if (mode === DETAIL_MODE.ADD_PIN_EXIST) {
        _pinObjects = allPinObject;
      }

      const viewer = getCurrentViewer();
      const is3dView = viewer?.model?.is3d();

      highlightPinObjects({
        pinObjects: _pinObjects,
        newPinObjectAdded,
        pinObjectSelected,
      });

      if (mode === DETAIL_MODE.DETAIL_PIN_GROUP && is3dView) {
        // use setTimeout to avoid conflict
        // with highlight logic when selecting pin in left sidebar
        setTimeout(() => {
          highlightTargetObjectsWithExcludeDbIds({
            targetObject,
            excludeDbIds: flatten(
              pinObjects.map((pinObject) => pinObject.dbIds)
            ),
          });
        }, 100);
      }

      return () => {
        // clear highlight by render proxy
        // active object using render proxy to highlight
        // because objects can be inside other objects
        // if use material to highlight, it will not be highlighted.
        // clear highlight by material
        // if (is3dView) {
        // clearHighlighTargetObjectByDbIds();
        // }
        clearHighlightPinObject();
      };
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      targetObject?.dbIds?.length,
      // eslint-disable-next-line
      useDeepCompareMemoize([allPinObject, pinObjects, newPinObjectAdded]),
      viewerAddPinType,
      isLoadedViewerModelData,
      clearHighlighTargetObjectByDbIds,
      highlightPinObjects,
      clearHighlightPinObject,
      highlightTargetObjectsWithExcludeDbIds,
      pinObjectSelected,
    ]
  );

  useEffect(() => {
    if (listSelected.length) return;
    let _pinObjects = pinObjects;
    // TODO: highlight all pin in addPinExist mode
    if (mode === DETAIL_MODE.ADD_PIN_EXIST) {
      _pinObjects = allPinObject;
    }

    highlightPinObjects({
      pinObjects: _pinObjects,
      newPinObjectAdded,
      pinObjectSelected,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSelected]);

  const { isDurationHighlight, message } = useContextInspectionDetail();
  const dispatchContext = useInspectionDetailDispatch();

  useEffect(() => {
    return () => {
      selectLabel([]);
    };
  }, []);

  // clear message when add pin success
  useEffect(() => {
    if (isDurationHighlight || message) {
      setTimeout(() => {
        dispatchContext({
          type: Types.SET_CURRENT_ADDED_PIN_ID_TO_GROUP,
          payload: undefined,
        });
        dispatchContext({
          type: Types.SET_ACTIVE_OBJECTS,
          payload: {
            objects: [],
            isDurationHighlight: false,
            message: undefined,
          },
        });
      }, DURATION_CLEAR_HIGHLIGHT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDurationHighlight, message]);

  useEffect(() => {
    if (pinSelected?.id) {
      const index = currentInspectionPins.findIndex(
        (pin) => pin.id === pinSelected.id
      );

      if (
        index === -1 &&
        (mode !== DETAIL_MODE.ADD_PIN_EXIST ||
          !listExistModePins.find((pin) => pin.id === pinSelected.id))
      ) {
        dispatch(clearPinSelected());
      } else if (index !== -1 && pinSelected.index !== index) {
        dispatch(updatePin({ id: pinSelected.id, index }));
      }
    }
  }, [
    dispatch,
    currentInspectionPins,
    pinSelected?.id,
    pinSelected?.index,
    pinSelected?.isPinOtherGroup,
    mode,
    listExistModePins,
  ]);

  return {
    pins: currentInspectionPins,
  };
};

export default usePins;
